/*
 * Tiny pixel-art toolkit. Sprites are authored as ASCII grids (one char per
 * pixel) plus a palette, rasterised once into offscreen canvases and cached.
 */

export type Palette = Record<string, string>;

export interface Sprite {
  canvas: HTMLCanvasElement;
  w: number;
  h: number;
}

export function createCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.ceil(w));
  c.height = Math.max(1, Math.ceil(h));
  return c;
}

export function ctx2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas not supported');
  ctx.imageSmoothingEnabled = false;
  return ctx;
}

const cache = new Map<string, Sprite>();

/** Rasterise an ASCII sprite. `.` and space are transparent. */
export function spriteFromGrid(key: string, rows: readonly string[], palette: Palette): Sprite {
  const hit = cache.get(key);
  if (hit) return hit;
  const h = rows.length;
  const w = Math.max(...rows.map((r) => r.length));
  const canvas = createCanvas(w, h);
  const ctx = ctx2d(canvas);
  for (let y = 0; y < h; y++) {
    const row = rows[y] ?? '';
    for (let x = 0; x < row.length; x++) {
      const ch = row[x];
      if (!ch || ch === '.' || ch === ' ') continue;
      const color = palette[ch];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect(x, y, 1, 1);
    }
  }
  const sprite = { canvas, w, h };
  cache.set(key, sprite);
  return sprite;
}

/** Drop cached sprites whose keys start with any prefix (per-customer art between shifts). */
export function pruneSprites(prefixes: readonly string[]): void {
  for (const key of cache.keys()) {
    if (prefixes.some((p) => key.startsWith(p))) cache.delete(key);
  }
}

/** Draw into a cached sprite with arbitrary code (for procedural art). */
export function spriteFromDraw(
  key: string,
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D) => void,
): Sprite {
  const hit = cache.get(key);
  if (hit) return hit;
  const canvas = createCanvas(w, h);
  draw(ctx2d(canvas));
  const sprite = { canvas, w, h };
  cache.set(key, sprite);
  return sprite;
}

export function drawSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  x: number,
  y: number,
  opts: { flip?: boolean; scale?: number; alpha?: number } = {},
): void {
  const scale = opts.scale ?? 1;
  const prevAlpha = ctx.globalAlpha;
  if (opts.alpha !== undefined) ctx.globalAlpha = prevAlpha * opts.alpha;
  if (opts.flip) {
    ctx.save();
    ctx.translate(Math.round(x + sprite.w * scale), Math.round(y));
    ctx.scale(-1, 1);
    ctx.drawImage(sprite.canvas, 0, 0, sprite.w * scale, sprite.h * scale);
    ctx.restore();
  } else {
    ctx.drawImage(sprite.canvas, Math.round(x), Math.round(y), sprite.w * scale, sprite.h * scale);
  }
  ctx.globalAlpha = prevAlpha;
}

/** Tint a sprite's opaque pixels with a flat colour (silhouettes, flashes). */
export function tinted(sprite: Sprite, color: string, key: string): Sprite {
  return spriteFromDraw(`${key}:tint:${color}`, sprite.w, sprite.h, (ctx) => {
    ctx.drawImage(sprite.canvas, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, sprite.w, sprite.h);
  });
}

export function px(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

/** Deterministic hash → [0,1) for stable per-pixel noise. */
export function noise(x: number, y: number, seed = 0): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2246822519) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

export function mixColor(a: string, b: string, t: number): string {
  const pa = parseHex(a);
  const pb = parseHex(b);
  const k = Math.max(0, Math.min(1, t));
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * k);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * k);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * k);
  return `rgb(${r},${g},${bl})`;
}

function parseHex(hex: string): [number, number, number] {
  const m = hex.replace('#', '');
  const full =
    m.length === 3
      ? m
          .split('')
          .map((c) => c + c)
          .join('')
      : m;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Scale a sprite into a DOM canvas at an integer zoom (for buttons / icons). */
export function paintSpriteInto(canvas: HTMLCanvasElement, sprite: Sprite, zoom: number): void {
  canvas.width = sprite.w * zoom;
  canvas.height = sprite.h * zoom;
  const ctx = ctx2d(canvas);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(sprite.canvas, 0, 0, sprite.w * zoom, sprite.h * zoom);
}
